import { Application } from "@hotwired/stimulus"
import { identifierForGlobKey } from 'stimulus-vite-helpers'

// LAZY Stimulus registry. The usual `registerControllers(app, import.meta.globEager(...))`
// would eagerly import every controller (and its deps — tinymce alone is ~0.5MB) on every
// page. Instead we glob lazily and only import/register a controller the first time its
// identifier actually appears in the DOM. Three triggers cover all arrival paths:
//   1. initial scan (module runs after DOM parse — it's an ESM bundle, so the DOM is ready;
//      NEVER wait on DOMContentLoaded here, it may already have fired),
//   2. turbo:load (Frames/visits — Drive is off app-wide but Frames are still used),
//   3. a MutationObserver, because plenty of markup is injected AFTER load (Vue-rendered
//      lists, jQuery-inserted modals/partials, cocoon nested fields). Without it, a
//      data-controller inside injected HTML would never boot its controller. Stimulus
//      itself picks up new elements for ALREADY-registered identifiers; the observer is
//      only needed to trigger the first lazy import of not-yet-seen ones.
const application = Application.start()
const controllerLoaders = import.meta.glob('./**/*_controller.js')
const registeredControllers = new Set()
const controllerPathsByIdentifier = Object.fromEntries(
  Object.keys(controllerLoaders)
    .map(path => [identifierForGlobKey(path), path])
    .filter(([identifier]) => identifier)
)

function addIdentifiersFromValue(identifiers, value) {
  if (!value) return

  value.split(/\s+/).filter(Boolean).forEach(identifier => identifiers.add(identifier))
}

function controllerIdentifiersIn(element) {
  const identifiers = new Set()

  if (element.dataset) addIdentifiersFromValue(identifiers, element.dataset.controller)

  element.querySelectorAll('[data-controller]').forEach(controllerElement => {
    addIdentifiersFromValue(identifiers, controllerElement.dataset.controller)
  })

  return identifiers
}

async function registerController(identifier) {
  if (registeredControllers.has(identifier)) return

  const controllerPath = controllerPathsByIdentifier[identifier]
  if (!controllerPath) return

  // Mark BEFORE the (async) import so a second sighting during the await can't
  // double-register the same identifier.
  registeredControllers.add(identifier)

  const controllerModule = await controllerLoaders[controllerPath]()
  if (typeof controllerModule.default === 'function') {
    application.register(identifier, controllerModule.default)
  }
}

function loadControllersIn(element = document) {
  controllerIdentifiersIn(element).forEach(identifier => {
    registerController(identifier)
  })
}

loadControllersIn()
document.addEventListener('turbo:load', () => loadControllersIn())

new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) loadControllersIn(node)
    })
  })
}).observe(document.documentElement, { childList: true, subtree: true })
